import { displayNotification } from "@/utils/notifications";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { ErrorLink } from "@apollo/client/link/error";
import { from as observableFrom, switchMap } from 'rxjs';

let isRefreshing = false;
let pending: Array<() => void> = [];

const flushPending = () => {
  pending.forEach((cb) => cb());
  pending = [];
};

async function refreshToken(retries = 2, delayMs = 500): Promise<void> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(`${import.meta.env.VITE_DB_URL}/graphql`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', source: 'scheduler_dashboard' },
        credentials: 'include',
        body: JSON.stringify({
          query: 'mutation exchangeRefreshTokenForJwt { exchangeRefreshTokenForJwt }',
          operationName: 'exchangeRefreshTokenForJwt',
        }),
      });

      if (!res.ok) throw new Error('refresh_network_failed');

      const json = await res.json();
      if (json.errors?.length || json.data?.exchangeRefreshTokenForJwt !== true) {
        throw new Error('refresh_failed');
      }

      return;
    } catch (err: any) {
      if (err.message === 'refresh_failed' || attempt === retries) {
        throw err;
      }
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
}

const errorLink = new ErrorLink(({ error, operation, forward }) => {
  const isAuthError =
    CombinedGraphQLErrors.is(error) &&
    error.errors.some((e) => e.extensions?.code === 'UNAUTHENTICATED');

  if (!isAuthError) return;
  if (operation.operationName === 'exchangeRefreshTokenForJwt') return;

  if (!isRefreshing) {
    isRefreshing = true;
    return observableFrom(
      refreshToken()
        .then(() => flushPending())
        .catch((e) => {
          pending = [];
          window.location.assign('/login');
          displayNotification({ type: 'error', text: 'Please log in again.' });
          throw e;
        })
        .finally(() => {
          isRefreshing = false;
        })
    ).pipe(switchMap(() => forward(operation)));
  }

  // Refresh already running — queue, then replay.
  return observableFrom(
    new Promise<void>((resolve) => pending.push(resolve))
  ).pipe(switchMap(() => forward(operation)));
});

export default errorLink
